import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import cartService, {
  type CartDto,
  type CartItemDto,
  type AddCartItemRequest,
  type CartItemValidation,
  CartStatus
} from '@/services/cartService'
import { useSettingsStore } from '@/stores/settings'

// ============================================================================
// Types
// ============================================================================

// Result of stock validation when adding to cart
export interface AddToCartResult {
  success: boolean
  message?: string
  availableQuantity?: number
  itemId?: string // The ID of the created cart item (for linking extras)
}

// Result of basket validation before checkout
export interface BasketValidationResult {
  isValid: boolean
  invalidItems: CartItemValidation[]
  validItems: CartItemValidation[]
}

// Cart item with shop information for multi-shop support
// This maintains backward compatibility with existing components
export interface CartItem {
  id: string // Cart item ID from server (or productId-variantId locally)
  productId: string
  variantId?: string
  // Product info
  name: string
  variantName?: string // Variant/size name (e.g., "Large", "Red / XL")
  title: string
  slug: string
  imageUrl?: string
  price: number
  compareAtPrice?: number
  // Quantity
  quantity: number
  // Stock info
  currentStockQuantity?: number
  isInStock?: boolean
  // Shop info
  shopId: string
  shopCode: string
  shopName: string
  shopLogoUrl?: string
  // Dietary information
  isVegan?: boolean
  isVegetarian?: boolean
  isHalal?: boolean
  // Category information (for promo code validation)
  categoryId?: string
  // Extra (add-on) properties
  isExtra?: boolean
  parentCartItemId?: string
  productExtraId?: string
  isFreeExtra?: boolean
  // Option selections (e.g. "Spice level: Mild", "Topping: Cheese")
  optionSelections?: Array<{
    optionGroupId: string
    groupName?: string
    optionChoiceId: string
    choiceName?: string
    priceAdjustment?: number
  }>
}

// Group of items from the same shop
export interface ShopCartGroup {
  shopId: string
  shopCode: string
  shopName: string
  shopLogoUrl?: string
  // ISO 4217 currency code from the cart's shop (BE-authoritative).
  currency: string
  cartToken?: string // Server-side cart token for this shop
  items: CartItem[]
  subtotal: number
  itemCount: number
  expiresAt?: string
  secondsUntilExpiry?: number
}

// Storage key for cart tokens
const CART_TOKENS_KEY = 'shop_cart_tokens'

export const useCartStore = defineStore('cart', () => {
  // ============================================================================
  // State
  // ============================================================================

  // Map of shopId -> CartDto (server-side cart data)
  const shopCarts = ref<Map<string, CartDto>>(new Map())

  // Map of shopId -> cartToken for persistence
  const cartTokens = ref<Map<string, string>>(new Map())

  // Loading states
  const isLoading = ref(false)
  const isAddingItem = ref(false)

  // Error state — shown as inline banner in cart-related views
  const error = ref<string | null>(null)

  // Dropdown visibility
  const isOpen = ref(false)

  // Current shared cart being viewed (when viewing someone else's cart)
  const sharedCart = ref<CartDto | null>(null)

  // ============================================================================
  // Computed - Backward Compatible Interface
  // ============================================================================

  // Convert server carts to flat items array for backward compatibility
  const items = computed((): CartItem[] => {
    const allItems: CartItem[] = []
    shopCarts.value.forEach((cart) => {
      cart.items.forEach((item) => {
        allItems.push(mapServerItemToCartItem(item, cart))
      })
    })
    return allItems
  })

  // Total number of items in cart
  const itemCount = computed(() =>
    items.value.reduce((sum, item) => sum + item.quantity, 0)
  )

  // Total cart value
  const total = computed(() =>
    items.value.reduce((sum, item) => sum + (item.price * item.quantity), 0)
  )

  // Check if cart is empty
  const isEmpty = computed(() => items.value.length === 0)

  // Get unique shop count
  const shopCount = computed(() => shopCarts.value.size)

  // Group items by shop
  const itemsByShop = computed((): ShopCartGroup[] => {
    const groups: ShopCartGroup[] = []

    shopCarts.value.forEach((cart) => {
      groups.push({
        shopId: cart.shopId,
        shopCode: cart.shopCode,
        shopName: cart.shopName,
        shopLogoUrl: cart.shopLogoUrl,
        // BE-authoritative snapshot: CartDto.currency is non-nullable and
        // set from the shop when the cart is created. No FE fallback — a
        // missing currency here is a real bug, not a display concern.
        currency: cart.currency,
        cartToken: cart.cartToken,
        items: cart.items.map(item => mapServerItemToCartItem(item, cart)),
        subtotal: cart.subtotal,
        itemCount: cart.itemCount,
        expiresAt: cart.expiresAt,
        secondsUntilExpiry: cart.secondsUntilExpiry
      })
    })

    return groups
  })

  // ============================================================================
  // Helper Functions
  // ============================================================================

  function mapServerItemToCartItem(item: CartItemDto, cart: CartDto): CartItem {
    return {
      id: item.id,
      productId: item.productId,
      variantId: item.productVariantId,
      name: item.productName,
      variantName: item.variantName,
      title: item.variantName ? `${item.productName} - ${item.variantName}` : item.productName,
      slug: item.productSlug || '',
      imageUrl: item.productImageUrl,
      price: item.unitPrice,
      quantity: item.quantity,
      currentStockQuantity: item.currentStockQuantity,
      isInStock: item.isInStock,
      shopId: cart.shopId,
      shopCode: cart.shopCode,
      shopName: cart.shopName,
      shopLogoUrl: cart.shopLogoUrl,
      // Dietary information
      isVegan: item.isVegan,
      isVegetarian: item.isVegetarian,
      isHalal: item.isHalal,
      // Extra properties
      isExtra: item.isExtra,
      parentCartItemId: item.parentCartItemId,
      productExtraId: item.productExtraId,
      isFreeExtra: item.isFreeExtra,
      // Product option selections
      optionSelections: item.optionSelections
    }
  }

  function saveTokensToStorage() {
    try {
      const tokens: Record<string, string> = {}
      cartTokens.value.forEach((token, shopId) => {
        tokens[shopId] = token
      })
      localStorage.setItem(CART_TOKENS_KEY, JSON.stringify(tokens))
    } catch (error) {
      console.error('Failed to save cart tokens to storage:', error)
    }
  }

  function loadTokensFromStorage() {
    try {
      const stored = localStorage.getItem(CART_TOKENS_KEY)
      if (stored) {
        const tokens = JSON.parse(stored) as Record<string, string>
        cartTokens.value = new Map(Object.entries(tokens))
      }
    } catch (error) {
      console.error('Failed to load cart tokens from storage:', error)
      cartTokens.value = new Map()
    }
  }

  function removeExpiredTokens() {
    // Remove carts that have expired
    const expiredShopIds: string[] = []
    shopCarts.value.forEach((cart, shopId) => {
      if (cart.isExpired || cart.status !== CartStatus.Active) {
        expiredShopIds.push(shopId)
      }
    })

    expiredShopIds.forEach(shopId => {
      shopCarts.value.delete(shopId)
      cartTokens.value.delete(shopId)
    })

    if (expiredShopIds.length > 0) {
      saveTokensToStorage()
    }
  }

  // ============================================================================
  // Actions
  // ============================================================================

  /**
   * Initialize cart from storage - load tokens and fetch cart data
   */
  async function initializeCart() {
    loadTokensFromStorage()

    // Fetch cart data for each stored token
    const fetchPromises: Promise<void>[] = []
    cartTokens.value.forEach((token, shopId) => {
      fetchPromises.push(
        cartService.getCartByToken(token)
          .then(cart => {
            if (cart && cart.status === CartStatus.Active && !cart.isExpired) {
              shopCarts.value.set(shopId, cart)
            } else {
              // Remove invalid/expired cart
              cartTokens.value.delete(shopId)
            }
          })
          .catch((err) => {
            console.error('Failed to load cart for shop:', shopId, err)
            // Only remove token on 404 (cart not found) — keep token on network errors
            if (err?.response?.status === 404) {
              cartTokens.value.delete(shopId)
            } else {
              error.value = 'Unable to load your cart. Please refresh the page.'
            }
          })
      )
    })

    await Promise.all(fetchPromises)
    saveTokensToStorage()
    removeExpiredTokens()
  }

  /**
   * Get or create a cart for a specific shop
   */
  async function getOrCreateShopCart(shopId: string): Promise<CartDto | null> {
    try {
      isLoading.value = true

      // Check if we have a cached cart
      const existingCart = shopCarts.value.get(shopId)
      if (existingCart && !existingCart.isExpired) {
        return existingCart
      }

      // Check if we have a token
      const existingToken = cartTokens.value.get(shopId)
      if (existingToken) {
        const cart = await cartService.getCartByToken(existingToken)
        if (cart && cart.status === CartStatus.Active && !cart.isExpired) {
          shopCarts.value.set(shopId, cart)
          return cart
        }
      }

      // Create new cart via API
      const cart = await cartService.getOrCreateCart(shopId)
      shopCarts.value.set(shopId, cart)
      cartTokens.value.set(shopId, cart.cartToken)
      saveTokensToStorage()
      return cart
    } catch (error) {
      console.error('Failed to get or create cart:', error)
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Add item to cart (with stock validation)
   * For extras, pass isExtra=true, parentCartItemId, and productExtraId
   */
  async function validateAndAddItem(
    item: Omit<CartItem, 'id'>,
    quantity: number = 1,
    extraOptions?: {
      isExtra?: boolean
      parentCartItemId?: string
      productExtraId?: string
      optionSelections?: { optionGroupId: string; optionChoiceId: string }[]
    }
  ): Promise<AddToCartResult> {
    try {
      isAddingItem.value = true

      // Single-shop basket restriction: prevent adding items from different shops (unless multi-shop enabled)
      const settingsStore = useSettingsStore()
      if (!settingsStore.allowMultiShopBasket && shopCarts.value.size > 0 && !shopCarts.value.has(item.shopId)) {
        const existingShop = Array.from(shopCarts.value.values())[0]
        return {
          success: false,
          message: `You already have items from ${existingShop.shopName} in your basket. Please complete that order first or clear your basket to order from ${item.shopName}.`
        }
      }

      // Get or create cart for this shop
      let cart = shopCarts.value.get(item.shopId)

      if (!cart) {
        const newCart = await getOrCreateShopCart(item.shopId)
        if (!newCart) {
          return { success: false, message: 'Failed to create cart' }
        }
        cart = newCart
      }

      // Add item to cart via API
      const request: AddCartItemRequest = {
        productId: item.productId,
        productVariantId: item.variantId,
        quantity,
        // Extra fields
        isExtra: extraOptions?.isExtra,
        parentCartItemId: extraOptions?.parentCartItemId,
        productExtraId: extraOptions?.productExtraId,
        // Product option selections
        optionSelections: extraOptions?.optionSelections
      }

      const response = await cartService.addItem(cart.cartToken, request)

      if (!response.success) {
        return {
          success: false,
          message: response.message || 'Failed to add item to cart'
        }
      }

      // Update local cart state
      if (response.cart) {
        // Ensure option selections are on the cart item (API should return them, fallback to local data)
        if (extraOptions?.optionSelections?.length && response.item?.id) {
          const cartItem = response.cart.items?.find((i: any) => i.id === response.item?.id)
          if (cartItem && (!cartItem.optionSelections || cartItem.optionSelections.length === 0)) {
            cartItem.optionSelections = extraOptions.optionSelections
          }
        }
        shopCarts.value.set(item.shopId, response.cart)
      }

      // Return the created item ID for linking extras
      return {
        success: true,
        itemId: response.item?.id
      }
    } catch (error: any) {
      console.error('Error adding item to cart:', error)
      console.error('Response data:', error.response?.data)
      const errorMessage = error.response?.data?.message
        || error.response?.data?.Message // Handle PascalCase
        || (typeof error.response?.data === 'string' ? error.response.data : null)
        || 'Failed to add item to cart'
      return {
        success: false,
        message: errorMessage
      }
    } finally {
      isAddingItem.value = false
    }
  }

  /**
   * Add item to cart (simple version for backward compatibility)
   */
  async function addItem(item: Omit<CartItem, 'id'>, quantity: number = 1) {
    await validateAndAddItem(item, quantity)
  }

  /**
   * Remove item from cart
   */
  async function removeItem(itemId: string) {
    // Find which shop cart contains this item
    let targetCart: CartDto | undefined
    let targetShopId: string | undefined

    shopCarts.value.forEach((cart, shopId) => {
      const hasItem = cart.items.some(i => i.id === itemId)
      if (hasItem) {
        targetCart = cart
        targetShopId = shopId
      }
    })

    if (!targetCart || !targetShopId) {
      console.warn('Item not found in any cart:', itemId)
      return
    }

    try {
      const response = await cartService.removeItem(targetCart.cartToken, itemId)
      if (response.cart) {
        if (response.cart.items.length === 0) {
          // Remove empty cart
          shopCarts.value.delete(targetShopId)
          cartTokens.value.delete(targetShopId)
          saveTokensToStorage()
        } else {
          shopCarts.value.set(targetShopId, response.cart)
        }
      }
    } catch (error) {
      console.error('Failed to remove item:', error)
    }
  }

  /**
   * Update item quantity
   */
  async function updateQuantity(itemId: string, quantity: number) {
    if (quantity <= 0) {
      await removeItem(itemId)
      return
    }


    // Find which shop cart contains this item
    let targetCart: CartDto | undefined
    let targetShopId: string | undefined

    shopCarts.value.forEach((cart, shopId) => {
      const hasItem = cart.items.some(i => i.id === itemId)
      if (hasItem) {
        targetCart = cart
        targetShopId = shopId
      }
    })

    if (!targetCart || !targetShopId) {
      console.warn('Item not found in any cart:', itemId)
      return
    }

    try {
      const response = await cartService.updateItemQuantity(
        targetCart.cartToken,
        itemId,
        { quantity }
      )

      if (response.cart) {
        shopCarts.value.set(targetShopId, response.cart)
      }
    } catch (error) {
      console.error('Failed to update quantity:', error)
    }
  }

  /**
   * Increment item quantity
   */
  async function incrementQuantity(itemId: string) {
    const item = items.value.find(i => i.id === itemId)
    if (item) {
      await updateQuantity(itemId, item.quantity + 1)
    }
  }

  /**
   * Decrement item quantity
   */
  async function decrementQuantity(itemId: string) {
    const item = items.value.find(i => i.id === itemId)
    if (item) {
      await updateQuantity(itemId, item.quantity - 1)
    }
  }

  /**
   * Clear all items from a specific shop's cart
   */
  async function clearShop(shopId: string) {
    const cart = shopCarts.value.get(shopId)
    if (!cart) return

    try {
      await cartService.clearCart(cart.cartToken)
      shopCarts.value.delete(shopId)
      cartTokens.value.delete(shopId)
      saveTokensToStorage()
    } catch (error) {
      console.error('Failed to clear shop cart:', error)
    }
  }

  /**
   * Clear entire cart (all shops)
   */
  async function clearCart() {
    const clearPromises: Promise<void>[] = []

    shopCarts.value.forEach((cart) => {
      clearPromises.push(
        cartService.clearCart(cart.cartToken)
          .then(() => { /* success */ })
          .catch(err => {
            console.error('Failed to clear cart:', err)
          })
      )
    })

    await Promise.all(clearPromises)
    shopCarts.value.clear()
    cartTokens.value.clear()
    saveTokensToStorage()
  }

  /**
   * Get items for a specific shop
   */
  function getShopItems(shopId: string): CartItem[] {
    return items.value.filter(item => item.shopId === shopId)
  }

  /**
   * Get subtotal for a specific shop
   */
  function getShopSubtotal(shopId: string): number {
    const cart = shopCarts.value.get(shopId)
    return cart?.subtotal || 0
  }

  /**
   * Get cart token for a shop
   */
  function getCartToken(shopId: string): string | undefined {
    return cartTokens.value.get(shopId)
  }

  /**
   * Get cart by token (for shared cart viewing)
   */
  async function loadSharedCart(cartToken: string): Promise<CartDto | null> {
    try {
      isLoading.value = true
      const cart = await cartService.getCartByToken(cartToken)
      if (cart) {
        sharedCart.value = cart
      }
      return cart
    } catch (error) {
      console.error('Failed to load shared cart:', error)
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Clear shared cart view
   */
  function clearSharedCart() {
    sharedCart.value = null
  }

  /**
   * Copy shared cart to own cart (take ownership)
   */
  async function copySharedCartToOwn(): Promise<{ success: boolean; message?: string }> {
    if (!sharedCart.value) return { success: false, message: 'No shared cart to copy' }

    // Single-shop basket restriction: prevent copying if cart already has items from different shop (unless multi-shop enabled)
    const settingsStore = useSettingsStore()
    if (!settingsStore.allowMultiShopBasket && shopCarts.value.size > 0 && !shopCarts.value.has(sharedCart.value.shopId)) {
      const existingShop = Array.from(shopCarts.value.values())[0]
      return {
        success: false,
        message: `You already have items from ${existingShop.shopName} in your basket. Please complete that order first or clear your basket.`
      }
    }

    try {
      // Store the token and cart data
      const cart = sharedCart.value
      shopCarts.value.set(cart.shopId, cart)
      cartTokens.value.set(cart.shopId, cart.cartToken)
      saveTokensToStorage()

      // Try to associate with customer (if logged in)
      try {
        await cartService.associateWithCustomer(cart.cartToken)
      } catch {
        // Ignore if not logged in
      }

      sharedCart.value = null
      return { success: true }
    } catch (error) {
      console.error('Failed to copy shared cart:', error)
      return { success: false, message: 'Failed to copy shared cart' }
    }
  }

  /**
   * Get shareable link for a shop's cart
   */
  async function getShareableLink(shopId: string): Promise<string | null> {
    const cart = shopCarts.value.get(shopId)
    if (!cart) return null

    try {
      const response = await cartService.getShareableLink(cart.cartToken)
      return response?.shareableUrl || null
    } catch (error) {
      console.error('Failed to get shareable link:', error)
      return null
    }
  }

  /**
   * Extend cart expiration
   */
  async function extendExpiration(shopId: string): Promise<boolean> {
    const cart = shopCarts.value.get(shopId)
    if (!cart) return false

    try {
      const response = await cartService.extendExpiration(cart.cartToken)
      if (response.cart) {
        shopCarts.value.set(shopId, response.cart)
      }
      return response.success
    } catch (error) {
      console.error('Failed to extend cart expiration:', error)
      return false
    }
  }

  // ============================================================================
  // Dropdown Controls
  // ============================================================================

  function openDropdown() {
    isOpen.value = true
  }

  function closeDropdown() {
    isOpen.value = false
  }

  function toggleDropdown() {
    isOpen.value = !isOpen.value
  }

  // ============================================================================
  // Stock Validation
  // ============================================================================

  /**
   * Validate all items in a shop's cart before checkout.
   * Returns which items are valid/invalid.
   */
  async function validateShopBasket(shopId: string): Promise<BasketValidationResult> {
    const cart = shopCarts.value.get(shopId)

    if (!cart || cart.items.length === 0) {
      return { isValid: true, invalidItems: [], validItems: [] }
    }

    try {
      const result = await cartService.validateCart(cart.cartToken)

      const invalidItems = result.items.filter(i => !i.isValid)
      const validItems = result.items.filter(i => i.isValid)

      return {
        isValid: result.isValid,
        invalidItems,
        validItems
      }
    } catch (error) {
      console.error('Error validating basket:', error)
      // FAIL CLOSED: if validation fails, assume invalid — never let unvalidated cart through
      return {
        isValid: false,
        invalidItems: [],
        validItems: []
      }
    }
  }

  /**
   * Apply validation result - remove/adjust invalid items
   */
  async function applyValidationResult(_shopId: string, validationItems: CartItemValidation[]) {
    for (const validationItem of validationItems) {
      if (!validationItem.isValid || validationItem.availableQuantity === 0) {
        await removeItem(validationItem.itemId)
      } else if (validationItem.availableQuantity < validationItem.requestedQuantity) {
        await updateQuantity(validationItem.itemId, validationItem.availableQuantity)
      }
    }
  }

  // ============================================================================
  // Persistence (for backward compatibility)
  // ============================================================================

  function saveToStorage() {
    saveTokensToStorage()
  }

  /**
   * Clear all cart tokens from storage (for debugging)
   */
  function clearAllTokens() {
    shopCarts.value.clear()
    cartTokens.value.clear()
    localStorage.removeItem(CART_TOKENS_KEY)
    console.log('All cart tokens cleared')
  }

  /**
   * Reset the cart store (called on login/logout to prevent data leakage)
   */
  function resetStore() {
    shopCarts.value.clear()
    cartTokens.value.clear()
    sharedCart.value = null
    isOpen.value = false
    isLoading.value = false
    isAddingItem.value = false
    localStorage.removeItem(CART_TOKENS_KEY)
    console.log('✅ Cart store reset')
  }

  // ============================================================================
  // Return
  // ============================================================================

  return {
    // State
    items,
    isOpen,
    isLoading,
    isAddingItem,
    error,
    shopCarts,
    sharedCart,

    // Computed
    itemCount,
    total,
    isEmpty,
    shopCount,
    itemsByShop,

    // Actions
    initializeCart,
    getOrCreateShopCart,
    addItem,
    removeItem,
    updateQuantity,
    incrementQuantity,
    decrementQuantity,
    clearShop,
    clearCart,
    getShopItems,
    getShopSubtotal,
    getCartToken,

    // Stock Validation
    validateAndAddItem,
    validateShopBasket,
    applyValidationResult,

    // Sharing
    loadSharedCart,
    clearSharedCart,
    copySharedCartToOwn,
    getShareableLink,
    extendExpiration,

    // Dropdown
    openDropdown,
    closeDropdown,
    toggleDropdown,

    // Persistence
    saveToStorage,
    clearAllTokens,
    resetStore
  }
})
